import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase, runSupabaseWakeUp } from '../lib/supabase';

function formatUser(u) {
  if (!u) return null;
  return {
    id: u.id,
    email: u.email || u.user_metadata?.email,
    metadataRole: u.user_metadata?.role ?? null,
  };
}

function dumpAuthLocalStorage(prefix) {
  try {
    const storageKey = 'supabase.auth.token';
    const keysToCheck = [storageKey, `${storageKey}-code-verifier`, `${storageKey}-user`];
    const dump = {};
    keysToCheck.forEach((k) => {
      const v = window.localStorage?.getItem(k);
      dump[k] = v == null ? null : { len: v.length };
    });
    console.log('[AuthButtonsTest]', prefix, dump);
    return dump;
  } catch (e) {
    console.warn('[AuthButtonsTest] dumpAuthLocalStorage failed:', e?.message || e);
    return null;
  }
}

export default function AuthButtonsTest() {
  const { user, profile, role, signIn, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const userSummary = useMemo(() => formatUser(user), [user]);

  useEffect(() => {
    console.log('[AuthButtonsTest] mount', {
      hasUser: !!user,
      userSummary,
      role,
      loading,
      profile: profile ? { id: profile.id, role: profile.role, user_role: profile.user_role } : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    console.log('[AuthButtonsTest] auth changed', {
      hasUser: !!userSummary,
      userSummary,
      role,
      profileRole: profile?.role ?? null,
      profileUserRole: profile?.user_role ?? null,
      loading,
    });
  }, [userSummary, role, profile, loading]);

  async function handleCheckSession() {
    try {
      console.log('[AuthButtonsTest] check session start');
      setStatus('Checking session...');
      setError('');
      const { data, error: sessErr } = await supabase.auth.getSession();
      if (sessErr) throw sessErr;
      console.log('[AuthButtonsTest] check session result', { user: formatUser(data.session?.user), hasSession: !!data.session });
      setStatus('Session check done (see console for details).');
    } catch (e) {
      console.error('[AuthButtonsTest] check session error', e);
      setError(e?.message || 'Session check failed');
      setStatus('');
    }
  }

  async function handleLogin(e) {
    e.preventDefault();
    console.log('[AuthButtonsTest] login start', { email: email.trim(), passwordLen: password?.length });
    setStatus('Signing in...');
    setError('');
    try {
      const result = await signIn({ email: email.trim(), password });
      console.log('[AuthButtonsTest] login success', { resultKeys: result ? Object.keys(result) : null, user: result?.user?.id || result?.user?.email || result?.user_metadata?.sub });
      setStatus('Login request returned. Check console for Supabase responses.');
    } catch (err) {
      console.error('[AuthButtonsTest] login error', err);
      setError(err?.message || 'Login failed');
      setStatus('');
    }
  }

  async function handleLogout() {
    console.log('[AuthButtonsTest] logout start', {
      userId: user?.id,
      userEmail: user?.email,
      loading,
      visibilityState: document.visibilityState,
    });
    dumpAuthLocalStorage('before signOut()');
    setStatus('Signing out...');
    setError('');
    try {
      await runSupabaseWakeUp().catch(() => {});

      // Supabase docs: signOut({ scope }) removes session from browser and clears localstorage.
      // Using scope: 'local' signs out only the current session.
      const signOutPromise = supabase.auth.signOut({ scope: 'local' });
      const pendingWarnId = setTimeout(() => {
        console.warn('[AuthButtonsTest] signOut still pending after 12s');
      }, 12000);

      await signOutPromise
        .then(() => {
          clearTimeout(pendingWarnId);
          console.log('[AuthButtonsTest] supabase.auth.signOut resolved');
        })
        .catch((err) => {
          clearTimeout(pendingWarnId);
          console.error('[AuthButtonsTest] supabase.auth.signOut error:', err);
          throw err;
        });

      dumpAuthLocalStorage('after signOut()');
      const { data, error } = await supabase.auth.getSession();
      console.log('[AuthButtonsTest] after signOut getSession', {
        hasSession: !!data?.session,
        userId: data?.session?.user?.id ?? null,
        error: error?.message,
      });

      console.log('[AuthButtonsTest] logout finished (auth state should update via onAuthStateChange)');
      setStatus('Logout finished. Check user/role on page (updates via AuthContext).');
    } catch (err) {
      console.error('[AuthButtonsTest] logout error', err);
      setError(err?.message || 'Logout failed');
      setStatus('');
    }
  }

  return (
    <div style={{ padding: '1rem' }}>
      <h2 style={{ marginTop: 0 }}>Auth Buttons Test</h2>

      <div style={{ marginBottom: '1rem' }}>
        <div>
          <strong>Role:</strong> {role ?? 'null'}
        </div>
        <div>
          <strong>Auth loading:</strong> {String(loading)}
        </div>
        <div>
          <strong>User:</strong> {userSummary ? `${userSummary.id} (${userSummary.email || 'no-email'})` : 'null'}
        </div>
        <div>
          <strong>Profile.role:</strong> {profile?.role ?? 'null'}; <strong>Profile.user_role:</strong> {profile?.user_role ?? 'null'}
        </div>
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <button type="button" onClick={handleCheckSession} style={{ marginRight: '0.5rem' }}>
          Check Supabase Session
        </button>
        {user ? (
          <button type="button" onClick={handleLogout}>
            Logout
          </button>
        ) : null}
      </div>

      {user ? (
        <div style={{ color: 'var(--text-muted, #666)' }}>
          Logged in. Use <code>Logout</code> then try logging in again below.
        </div>
      ) : (
        <form onSubmit={handleLogin} style={{ maxWidth: 420 }}>
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            <div style={{ display: 'grid', gap: '0.25rem' }}>
              <label htmlFor="auth-test-email">Email</label>
              <input id="auth-test-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>

            <div style={{ display: 'grid', gap: '0.25rem' }}>
              <label htmlFor="auth-test-password">Password</label>
              <input id="auth-test-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>

            {error ? <div style={{ color: 'crimson' }}>{error}</div> : null}
            {status ? <div style={{ color: 'var(--text-muted, #666)' }}>{status}</div> : null}

            <button type="submit">Login</button>
          </div>
        </form>
      )}
    </div>
  );
}

